import { useEffect, useRef } from "react";

type Corner = "tl" | "tr" | "br" | "bl";

const CORNER_SEQUENCE: Corner[] = ["tl", "tr", "br", "bl"];
const SEQUENCE_WINDOW_MS = 5000;
const CORNER_INSET_PX = 80;

function cornerFromPoint(x: number, y: number): Corner | null {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (x <= CORNER_INSET_PX && y <= CORNER_INSET_PX) return "tl";
  if (x >= w - CORNER_INSET_PX && y <= CORNER_INSET_PX) return "tr";
  if (x >= w - CORNER_INSET_PX && y >= h - CORNER_INSET_PX) return "br";
  if (x <= CORNER_INSET_PX && y >= h - CORNER_INSET_PX) return "bl";
  return null;
}

/**
 * Discreet kiosk exit: Ctrl+Shift+E (keyboard) or four-corner taps in order
 * (top-left → top-right → bottom-right → bottom-left).
 */
export function useKioskSecretExit(
  onExit: () => void,
  enabled: boolean,
): void {
  const indexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  useEffect(() => {
    if (!enabled) return;

    const reset = () => {
      indexRef.current = 0;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const advance = () => {
      indexRef.current += 1;
      if (indexRef.current >= CORNER_SEQUENCE.length) {
        reset();
        onExitRef.current();
        return;
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(reset, SEQUENCE_WINDOW_MS);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "e") {
        e.preventDefault();
        reset();
        onExitRef.current();
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      const corner = cornerFromPoint(e.clientX, e.clientY);
      if (!corner) {
        reset();
        return;
      }
      const expected = CORNER_SEQUENCE[indexRef.current];
      if (corner === expected) {
        advance();
      } else {
        reset();
        if (corner === CORNER_SEQUENCE[0]) advance();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown);
      reset();
    };
  }, [enabled]);
}
